import {
	AssignStatus,
	InclusionFilterStatus,
	PriorityFilterStatus,
	SelectType,
	SignalFilterStatus,
} from "@/enum/list.enum";
import { ASSIGNED_TAB_STATUSES } from "@/pages/Private/pages/List/pages/Unassigned/assignedTabStatuses";

import { BulkChangeStatus } from "@/pages/Private/pages/List/schema/list";

import { FilterUnassignedTable } from "../unassigned-table.types";

interface BulkStatusScopeInput {
	selectType?: SelectType;
	filter?: FilterUnassignedTable;
	inclusionStatus?: InclusionFilterStatus[];
	/** Encoded "STATUS:filterParam" reason sub-filters of the grouped Show dropdown (PREC-3536). */
	exclusionReasons?: string[];
	priorityFilterStatus?: PriorityFilterStatus[];
	signalFilterStatus?: SignalFilterStatus[];
	/** The table's search term (PREC-4441). */
	searchValue?: string;
	/** The tab's status, as the table's row fetch uses it (PREC-4441). */
	assignStatus?: AssignStatus | null;
}

/** The statuses the table's rows are fetched with: the Assigned tab stands for several. */
export const tabAssignStatuses = (
	assignStatus?: AssignStatus | null
): AssignStatus[] | undefined =>
	assignStatus === AssignStatus.ASSIGNED
		? ASSIGNED_TAB_STATUSES
		: assignStatus
			? [assignStatus]
			: undefined;

/**
 * The filter scope every `bulkChangeStatus` call must carry.
 *
 * A select-all sends no ids: the backend re-derives the selection from these filters
 * (`ListBulkStatusService.bulkChangeStatus`). Omitting a single dimension therefore widens the
 * mutation to the whole bucket — filtering to "Only Disqualified leads" and reverting the campaign
 * assignment for all of them reverted every assigned lead in the list, because
 * `priorityFilterStatus` never left the frontend.
 *
 * The search term and the tab's statuses belong to it as much as the filters do: without them a
 * select-all made while searching, or on another tab than New, moved leads the table did not show
 * (PREC-4441).
 *
 * Returning the whole block from one place keeps the call sites from cherry-picking a subset.
 */
export type BulkStatusScope = Pick<
	BulkChangeStatus,
	| "selectType"
	| "filter"
	| "inclusionFilterStatus"
	| "exclusionReasons"
	| "priorityFilterStatus"
	| "signalFilterStatus"
	| "searchValue"
	| "assignStatuses"
>;

export const getBulkStatusScope = ({
	selectType,
	filter,
	inclusionStatus,
	exclusionReasons,
	priorityFilterStatus,
	signalFilterStatus,
	searchValue,
	assignStatus,
}: BulkStatusScopeInput): BulkStatusScope => ({
	selectType,
	filter,
	inclusionFilterStatus: inclusionStatus ?? undefined,
	exclusionReasons: exclusionReasons && exclusionReasons.length > 0 ? exclusionReasons : undefined,
	priorityFilterStatus: priorityFilterStatus ?? undefined,
	signalFilterStatus: signalFilterStatus ?? undefined,
	searchValue: searchValue?.trim() || undefined,
	assignStatuses: tabAssignStatuses(assignStatus),
});

/**
 * The scope fields every select-all bulk request carries besides its own filters: the tab's
 * statuses, the reason sub-filters, the signal filter and the search. A request built without them
 * covers leads the table hid (PREC-4450): an Assigned-tab select-all skipped the running and
 * replied leads, and a reason sub-filter covered the whole bucket.
 */
export const selectAllRequestScope = (scope: BulkStatusScope) => ({
	assignStatuses: scope.assignStatuses,
	exclusionReasons: scope.exclusionReasons,
	signalFilterStatus: scope.signalFilterStatus,
	searchValue: scope.searchValue,
});

/**
 * The table's whole view, whatever is selected: what a column with "Apply to: currently filtered"
 * is applied to, re-derived on the server. The table built it by hand without the reason
 * sub-filter, the signal filter or the Assigned tab's paused, message-sent and done leads, so a
 * column under "Unsure → company" ran for every Unsure lead (PREC-4455).
 */
export const currentViewFilters = (scope: BulkStatusScope) => ({
	assignStatus: scope.assignStatuses,
	filter: scope.filter,
	inclusionFilterStatus: scope.inclusionFilterStatus,
	// Inside the view, so a dialog that does not pass its own priority prop still narrows by it.
	priorityFilterStatus: scope.priorityFilterStatus,
	...selectAllRequestScope(scope),
});

/**
 * The filters of a select-all for an endpoint that takes the tab's statuses as `assignStatus` (Add
 * to CRM, Add to another list): nothing for a page selection, which names its ids, and the table's
 * whole view for a select-all. Both sent a hand-written Assigned list that left out paused,
 * message-sent and done leads, and no reason sub-filter, so a select-all move under one reason
 * took the whole bucket off the list (PREC-4450).
 */
export const selectAllViewFilters = (scope: BulkStatusScope) =>
	scope.selectType === SelectType.ALL
		? {
				assignStatus: scope.assignStatuses,
				inclusionFilterStatus: scope.inclusionFilterStatus,
				priorityFilterStatus: scope.priorityFilterStatus,
				signalFilterStatus: scope.signalFilterStatus,
				exclusionReasons: scope.exclusionReasons,
				filter: scope.filter,
				searchValue: scope.searchValue,
			}
		: {};
